import type { HuginConfig } from './config';
import { postBatch, postIdentify } from './client';
import { getOrCreateAnonymousId, reconcileAnonymousId } from './identity';
import { decorateUrl as decorateUrlPure } from './utm';
import type {
  EventType,
  FirstTouchContext,
  HuginApi,
  IdentifyOptions,
  IdentifyPayload,
  TrackEventItem,
  TrackEventsBatch,
} from './types';

export const TRACKING_DISABLED_KEY = 'ts_tracking_disabled';

export function isTrackingDisabled(): boolean {
  try {
    return !!localStorage.getItem(TRACKING_DISABLED_KEY);
  } catch {
    return false;
  }
}

export function hasDoNotTrack(): boolean {
  const dnt = window.doNotTrack ?? navigator.doNotTrack;
  return dnt === '1' || dnt === 'yes';
}

function revokeTrackingConsent(): void {
  try {
    localStorage.setItem(TRACKING_DISABLED_KEY, '1');
  } catch {
    // localStorage may be unavailable (private mode, blocked storage).
  }
}

function onDomReady(fn: () => void): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  } else {
    fn();
  }
}

function getTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

function buildEvent(
  eventType: EventType,
  properties?: Record<string, unknown>,
): TrackEventItem {
  return {
    eventId: crypto.randomUUID(),
    occurredAt: new Date().toISOString(),
    eventType,
    pageUrl: location.href,
    pageTitle: document.title || undefined,
    ...(properties ? { properties } : {}),
  };
}

export function buildTrackEventsBatch(
  config: HuginConfig,
  anonymousId: string,
  firstTouchContext: FirstTouchContext,
  event: TrackEventItem,
): TrackEventsBatch {
  const timezone = getTimezone();

  return {
    anonymousId,
    source: config.source,
    platform: 'web',
    sentAt: new Date().toISOString(),
    ...(timezone ? { timezone } : {}),
    ...firstTouchContext,
    events: [event],
  };
}

export function createHugin(
  config: HuginConfig,
  initialAnonymousId: string,
  firstTouchContext: FirstTouchContext,
): HuginApi {
  let anonymousId = initialAnonymousId;

  function track(
    eventType: EventType,
    properties?: Record<string, unknown>,
  ): void {
    if (isTrackingDisabled()) return;

    const batch = buildTrackEventsBatch(
      config,
      anonymousId,
      firstTouchContext,
      buildEvent(eventType, properties),
    );
    void postBatch(config.apiBaseUrl, batch).then((res) => {
      if (res) {
        anonymousId = reconcileAnonymousId(
          anonymousId,
          res.anonymousId,
          config.cookieDomain,
        );
      }
    });
  }

  function identify(identifiedUserId: string, options?: IdentifyOptions): void {
    if (isTrackingDisabled()) return;

    const payload: IdentifyPayload = {
      anonymousId,
      identifiedUserId,
      source: config.source,
      ...options,
    };
    void postIdentify(config.apiBaseUrl, payload);
  }

  function decorateUrl(href: string): string {
    return decorateUrlPure(href, firstTouchContext);
  }

  function decorateLinks(selector: string): void {
    decorateLinksWithContext(firstTouchContext, selector);
  }

  return {
    track,
    identify,
    getAnonymousId: () => anonymousId,
    decorateUrl,
    decorateLinks,
    consent: () => {},
    revokeConsent: revokeTrackingConsent,
  };
}

export function activateHugin(
  config: HuginConfig,
  firstTouchContext: FirstTouchContext,
): HuginApi {
  const hugin = createHugin(
    config,
    getOrCreateAnonymousId(config.cookieDomain),
    firstTouchContext,
  );
  window.hugin = hugin;
  hugin.track('page_view');
  installNavigationHooks(() => hugin.track('page_view'));
  installClickTracking(hugin.track);
  return hugin;
}

const HUGIN_LINK_CLASS = 'hugin-link';

function decorateAnchor(
  anchor: HTMLAnchorElement,
  firstTouchContext: FirstTouchContext,
): void {
  anchor.href = decorateUrlPure(anchor.href, firstTouchContext);
}

function decorateAnchors(
  firstTouchContext: FirstTouchContext,
  selector: string,
): void {
  document.querySelectorAll(selector).forEach((el) => {
    if (!(el instanceof HTMLAnchorElement) || !el.href) return;
    decorateAnchor(el, firstTouchContext);
  });
}

function anchorsForHuginLink(el: Element): HTMLAnchorElement[] {
  if (el instanceof HTMLAnchorElement) return el.href ? [el] : [];
  return [...el.querySelectorAll<HTMLAnchorElement>('a[href]')];
}

function decorateLinksWithContext(
  firstTouchContext: FirstTouchContext,
  selector: string,
): void {
  onDomReady(() => decorateAnchors(firstTouchContext, selector));
}

export function autoDecorateHuginLinks(
  firstTouchContext: FirstTouchContext,
): void {
  onDomReady(() => {
    document.querySelectorAll(`.${HUGIN_LINK_CLASS}`).forEach((el) => {
      anchorsForHuginLink(el).forEach((anchor) => {
        decorateAnchor(anchor, firstTouchContext);
      });
    });
  });
}

export function createDeferredHugin(
  config: HuginConfig,
  firstTouchContext: FirstTouchContext,
): HuginApi {
  let initialized = false;

  return {
    track: () => {},
    identify: () => {},
    getAnonymousId: () => '',
    decorateUrl: (href) => decorateUrlPure(href, firstTouchContext),
    decorateLinks: (selector) =>
      decorateLinksWithContext(firstTouchContext, selector),
    revokeConsent: revokeTrackingConsent,
    consent: () => {
      if (initialized || isTrackingDisabled()) return;
      initialized = true;
      activateHugin(config, firstTouchContext);
    },
  };
}

// --- DOM auto-tracking (navigation + declarative click events) ---

const HUGIN_EVENT_ATTR = 'data-hugin-event';
const HUGIN_EVENT_PROP_PREFIX = 'data-hugin-event-';

function resolveLabel(el: Element): string | undefined {
  const img = el.querySelector('img[alt]');
  if (img instanceof HTMLImageElement && img.alt) return img.alt;
  const aria = el.getAttribute('aria-label');
  if (aria) return aria;
  const text = el.textContent?.trim();
  return text || undefined;
}

function collectEventProperties(el: Element): Record<string, unknown> | undefined {
  const props: Record<string, unknown> = {};
  el.getAttributeNames().forEach((name) => {
    if (!name.startsWith(HUGIN_EVENT_PROP_PREFIX)) return;
    const value = el.getAttribute(name);
    if (value == null) return;
    props[name.slice(HUGIN_EVENT_PROP_PREFIX.length)] = value;
  });
  if (el instanceof HTMLAnchorElement && el.href) props.href = el.href;
  if (!props.label) {
    const label = resolveLabel(el);
    if (label) props.label = label;
  }
  return Object.keys(props).length ? props : undefined;
}

let clickHandler: ((e: MouseEvent) => void) | undefined;

export function installClickTracking(
  track: (eventType: EventType, props?: Record<string, unknown>) => void,
): () => void {
  if (clickHandler) {
    document.removeEventListener('click', clickHandler, true);
  }

  clickHandler = (e: MouseEvent) => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const el = target.closest(`[${HUGIN_EVENT_ATTR}]`);
    if (!el) return;
    const eventName = el.getAttribute(HUGIN_EVENT_ATTR);
    if (!eventName) return;
    track(eventName, collectEventProperties(el));
  };

  document.addEventListener('click', clickHandler, true);

  return () => {
    if (!clickHandler) return;
    document.removeEventListener('click', clickHandler, true);
    clickHandler = undefined;
  };
}

/** Removes the click-tracking listener. Intended for test isolation. */
export function resetClickTracking(): void {
  if (!clickHandler) return;
  document.removeEventListener('click', clickHandler, true);
  clickHandler = undefined;
}

/**
 * Hooks pushState, replaceState, and popstate so that SPA navigations
 * fire page_view. Deduplicates against the last tracked URL.
 */
export function installNavigationHooks(onPageView: () => void): void {
  let lastTrackedUrl = location.href;

  function onNavigate(url: string): void {
    if (url === lastTrackedUrl) return;
    lastTrackedUrl = url;
    onPageView();
  }

  function patchHistoryMethod(method: 'pushState' | 'replaceState'): void {
    const original = history[method].bind(history) as (
      data: unknown,
      unused: string,
      url?: string | URL | null,
    ) => void;
    history[method] = function (
      data: unknown,
      unused: string,
      url?: string | URL | null,
    ): void {
      original(data, unused, url);
      onNavigate(location.href);
    };
  }

  patchHistoryMethod('pushState');
  patchHistoryMethod('replaceState');

  window.addEventListener('popstate', () => {
    onNavigate(location.href);
  });
}
